import config from '../config';
import { getAuthToken } from './apiClient';

export interface UserDto {
  id: string;
  email: string;
  userName: string;
  emailConfirmed: boolean;
  roles: string[];
  createdOn: string;
}

function buildUrl(path: string): string {
  // Users endpoints are now served from the main API
  const left = config.apiBaseUrl.replace(/\/$/, '');
  const right = path.startsWith('/') ? path : `/${path}`;
  return `${left}${right}`;
}

async function getJson<T>(url: string): Promise<T> {
  const token = getAuthToken();
  const headers: HeadersInit = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = new Error(`Request failed with status ${response.status}`) as Error & { details?: unknown; status?: number };
    try {
      error.details = await response.json();
    } catch {
      // Ignore JSON parse errors
    }
    error.status = response.status;
    throw error;
  }

  return response.json() as Promise<T>;
}

async function deleteRequest(url: string): Promise<void> {
  const token = getAuthToken();
  const headers: HeadersInit = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const error = new Error(`Request failed with status ${response.status}`) as Error & { details?: unknown; status?: number };
    try {
      error.details = await response.json();
    } catch {
      // Ignore JSON parse errors
    }
    error.status = response.status;
    throw error;
  }
}

/**
 * Get all users (Admin only)
 */
export async function getAllUsers(): Promise<UserDto[]> {
  const url = buildUrl('/api/Users');
  return getJson<UserDto[]>(url);
}

/**
 * Get a specific user by ID (Admin only)
 */
export async function getUserById(id: string): Promise<UserDto> {
  const url = buildUrl(`/api/Users/${id}`);
  return getJson<UserDto>(url);
}

/**
 * Delete a user by ID (Admin only)
 */
export async function deleteUser(id: string): Promise<void> {
  const url = buildUrl(`/api/Users/${id}`);
  return deleteRequest(url);
}

/**
 * Check if the current user has the Admin role
 */
export function isCurrentUserAdmin(): boolean {
  const token = getAuthToken();
  if (!token) {
    return false;
  }

  try {
    const parts = token.split('.');
    if (parts.length < 2) {
      return false;
    }

    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    
    // Debug: log all claims to help identify the correct role claim
    console.log('[isCurrentUserAdmin] JWT payload:', payload);
    
    // Check for role claim - can be a string or array
    // .NET uses the full URI for ClaimTypes.Role
    const roleClaim = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || payload['role'];
    
    console.log('[isCurrentUserAdmin] Role claim:', roleClaim);
    
    if (Array.isArray(roleClaim)) {
      return roleClaim.includes('Admin');
    }
    
    return roleClaim === 'Admin';
  } catch (e) {
    console.error('Error parsing token for admin check:', e);
    return false;
  }
}
